import { useState, useCallback, useEffect } from 'react';
import type { ReactNode } from 'react';
import { apiGet, apiPost, ApiClientError } from '@client/lib/api';
import { SessionContext } from './session-context';
import type { Organization, SessionState, User } from './session-context';

interface SessionProviderProps {
  children: ReactNode;
}

/**
 * Provides session state to the app. On mount it calls GET /auth/session.
 * A 401 means the user is anonymous — that is a normal state, not an error.
 */
export function SessionProvider({ children }: SessionProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [status, setStatus] = useState<SessionState['status']>('loading');

  const refresh = useCallback(async () => {
    setStatus('loading');
    try {
      // GET /auth/session returns an ENVELOPE — { user, organization } — not a
      // bare user. Storing the envelope as `user` left user.role undefined.
      // Nothing read the role until the nav did, at which point default-deny
      // correctly classified the OWNER as crew and hid every screen but Today
      // and Calendar. The bug was here all along; reading the role exposed it.
      const session = await apiGet<{ user: User; organization: Organization | null }>(
        '/auth/session'
      );
      setUser(session.user);
      setOrganization(session.organization ?? null);
      setStatus('authenticated');
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'unauthorized') {
        setUser(null);
        setOrganization(null);
        setStatus('anonymous');
      } else {
        // Network or unexpected error — treat as anonymous rather than
        // blocking the UI on a transient failure.
        setUser(null);
        setOrganization(null);
        setStatus('anonymous');
      }
    }
  }, []);

  // Run the session check ON MOUNT. Without this the provider sits at its
  // initial 'loading' status forever and RequireAuth renders its spinner
  // indefinitely -- the app never reaches the login screen. The doc comment
  // above always claimed this happened; the effect was simply never written.
  useEffect(() => {
    void refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    try {
      await apiPost('/auth/logout');
    } catch {
      // Even if the server call fails, clear local state.
    }
    setUser(null);
    setOrganization(null);
    setStatus('anonymous');
  }, []);

  return (
    <SessionContext.Provider value={{ user, organization, status, refresh, logout }}>
      {children}
    </SessionContext.Provider>
  );
}
